import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useFormContext } from 'react-hook-form';
import { equalisations } from 'further-ui/domain';
import { FeesEqualisationTreatment } from 'further-types/lp/equalisation';
import { sumBy } from 'lodash';
import { EqualisationFeesFormValues } from '../Editor/EqualiseFees/FormWrapper';
import { useRowsEdit } from 'components/InteractiveTable';

interface UseEqualisationFeesProps {
  index: number;
  baseCurrency: string;
  editManyRows: ReturnType<typeof useRowsEdit>['editManyRows'];
  resetRowsEditModel: ReturnType<typeof useRowsEdit>['resetRowsEditModel'];
}

interface RecalculateFeesParams {
  value: EqualisationFeesFormValues['totalCommitmentFees'][number];
  treatment?: FeesEqualisationTreatment;
  percentage?: number;
  onChange?: (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
  ) => void;
  overrideEqualisationSalesTaxAmountLcy?: Record<string, number>;
  useOriginalFeeValues?: boolean;
}

const useEqualiseFees = ({
  index,
  baseCurrency,
  editManyRows,
  resetRowsEditModel,
}: UseEqualisationFeesProps) => {
  const apiRef = useGridApiRef();
  const { setValue, getValues } = useFormContext<EqualisationFeesFormValues>();

  const updateGridRows = (
    updatedValue: EqualisationFeesFormValues['totalCommitmentFees'][number],
  ) => {
    if (!apiRef.current) return;

    const { lpFees } = updatedValue;
    const updatedGridRows = lpFees.map((fee, feeIndex) => ({
      ...fee,
      id: fee.id || `${index}-${feeIndex}`,
      baseCurrency,
      rowIndex: feeIndex,
    }));

    apiRef.current?.setRows(updatedGridRows);

    lpFees.forEach((fee, feeIndex) => {
      const rowId = fee.id || `${index}-${feeIndex}`;

      apiRef.current?.setEditCellValue({
        id: rowId,
        field: 'equalisationFeeExTaxLcy',
        value: fee.equalisationFeeExTaxLcy,
      });

      apiRef.current?.setEditCellValue({
        id: rowId,
        field: 'equalisationSalesTaxAmountLcy',
        value: fee.equalisationSalesTaxAmountLcy,
      });
    });
  };

  const recalculateFees = ({
    value,
    treatment,
    percentage,
    onChange,
    useOriginalFeeValues,
  }: RecalculateFeesParams) => {
    if (!treatment) return;
    const recalculatedFees = (value?.lpFees || []).map((fee) => {
      const {
        equalisationFeeExTaxLcy,
        equalisationSalesTaxAmountLcy,
        equalisationFeeIncTaxLcy,
      } = equalisations.calculateEqualisationTreatmentOfFees({
        feeExTaxLcy: useOriginalFeeValues
          ? fee.feeExTaxLcy
          : fee.equalisationFeeExTaxLcy || 0,
        salesTaxPercentage: fee.salesTaxPercentage || 0,
        feesEqualisationTreatment: treatment,
        feesEqualisationTreatmentPercentage: percentage ?? 0,
      });

      return {
        ...fee,
        equalisationFeeExTaxLcy,
        equalisationSalesTaxAmountLcy,
        equalisationFeeIncTaxLcy,
      };
    });

    const totalEqualisationFeeIncTaxLcy = sumBy(
      recalculatedFees,
      (fee: { equalisationFeeIncTaxLcy: number }) =>
        fee.equalisationFeeIncTaxLcy,
    );

    const updatedValue = {
      ...value,
      lpFees: recalculatedFees,
      totalEqualisationFeeIncTaxLcy,
    };

    updateGridRows(updatedValue);

    onChange?.(updatedValue);
  };

  const updateSingleFeeField = (
    rowId: string,
    field: 'equalisationFeeExTaxLcy' | 'equalisationSalesTaxAmountLcy',
    newValue: number,
    rowIndex: number,
  ) => {
    setValue(
      `totalCommitmentFees.${index}.lpFees.${rowIndex}.${field}`,
      newValue,
    );

    const commitmentFees = getValues(`totalCommitmentFees.${index}`);

    if (field === 'equalisationFeeExTaxLcy') {
      recalculateFees({
        value: commitmentFees,
        treatment: commitmentFees.feesEqualisationTreatment,
        onChange: (newTotals) => {
          setValue(`totalCommitmentFees.${index}`, newTotals);
          apiRef.current?.setEditCellValue({
            id: rowId,
            field: 'equalisationSalesTaxAmountLcy',
            value: newTotals.lpFees[rowIndex].equalisationSalesTaxAmountLcy,
          });
        },
      });
    } else if (field === 'equalisationSalesTaxAmountLcy') {
      commitmentFees.lpFees[rowIndex].equalisationFeeIncTaxLcy =
        newValue + commitmentFees.lpFees[rowIndex].equalisationFeeExTaxLcy;
      commitmentFees.totalEqualisationFeeIncTaxLcy = sumBy(
        commitmentFees.lpFees,
        (fee) => fee.equalisationFeeIncTaxLcy,
      );
      setValue(`totalCommitmentFees.${index}`, commitmentFees);
      updateGridRows(commitmentFees);
    }
  };

  const recalculateFromTreatmentChange = (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
    treatment: FeesEqualisationTreatment,
    onChange: (
      value: EqualisationFeesFormValues['totalCommitmentFees'][number],
    ) => void,
    templateId?: string,
  ) => {
    if (treatment === FeesEqualisationTreatment.None) {
      onChange({
        ...value,
        feesEqualisationTreatment: treatment,
        feesEqualisationTreatmentPercentage: 0,
        lpFees: [],
        totalEqualisationFeeIncTaxLcy: 0,
      });
      resetRowsEditModel();
      setTimeout(() => {
        apiRef.current?.setRows([]);
      }, 25);
      return;
    }

    if (templateId) {
      const newLpFees = value.lpFeesByTemplate?.[templateId]?.lpFees || [];
      editManyRows(newLpFees.map((fee) => ({ id: fee.id })));
      recalculateFees({
        value: {
          ...value,
          lpFees: newLpFees,
        },
        useOriginalFeeValues: true,
        treatment,
        percentage: value?.feesEqualisationTreatmentPercentage,
        onChange: (updatedValue) =>
          onChange({
            ...updatedValue,
            feesEqualisationTreatment: treatment,
            ...(treatment === FeesEqualisationTreatment.Partial
              ? {
                  feesEqualisationTreatmentPercentage: 100,
                }
              : {}),
          }),
      });
    }
  };

  const recalculateFromPercentageChange = (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
    percentage: number,
    onChange: (
      value: EqualisationFeesFormValues['totalCommitmentFees'][number],
    ) => void,
  ) => {
    recalculateFees({
      value,
      useOriginalFeeValues: true,
      treatment:
        value?.feesEqualisationTreatment ?? FeesEqualisationTreatment.None,
      percentage,
      onChange: (updatedValue) => {
        onChange({
          ...updatedValue,
          feesEqualisationTreatmentPercentage: percentage,
        });
      },
    });
  };

  return {
    apiRef,
    recalculateFees,
    updateGridRows,
    updateSingleFeeField,
    recalculateFromTreatmentChange,
    recalculateFromPercentageChange,
  };
};

export default useEqualiseFees;
